import './searchResultDetails.scss';
import {useTranslation} from 'react-i18next';
import {DefaultRootState, useDispatch, useSelector} from 'react-redux';
import {
    useSelectedDocContext,
    useStateDocDetailsExpanded,
    useStateResultDetailsTab,
    useStateResultDetailsTabSize
} from '../../search-hooks';
import {changeUrl, expandDocumentDetailsAction, switchResultDetailsTabAction} from '../../duck/search-actions';
import {DetailsSection} from './details/detailsSection';
import {ProductDocument, TabType} from '../../search-types';
import classNames from 'clsx';
import {HibuButton} from '../../../common/hibuButton/hibuButton';
import {Icons} from '../../../icons/icons';
import {FileTypeColumnControl} from '../search-result-list/search-result-item/column-controls/fileType-column-control';
import {DeepReadonly} from 'utility-types';
import {Button} from 'react-bootstrap';
import React from 'react';
import {ContentSection} from './content/contentSection';
import {SpotlightSection} from './spotlight/spotlightSection';
import {SvgIcon} from '../../../icons/svgIcon';
import {SearchPageType} from '../../search-config';
import {NavigateFunction, useNavigate} from 'react-router-dom';
import {DetailsAssistant} from '../../../assistant/document-details-assistant/detailsAssistant';
import {GetState} from '../../../app-types';
import {DynamicSize} from '../../../common/dragable-divider/dragableDivider';
import {SearchPageAction} from '../../duck/search-types';
import {ResultItemViewConfig} from '../../../config/config-types';

const HIBU_FACET_COLUMN_MIN_WIDTH = 240;
const HIBU_RESULT_LIST_COLUMN_MIN_WIDTH = 270;
const SEARCH_PREVIEW_MIN_WIDTH = 600;
const COLLAPSED_WIDTH = 62;
const MIN_WIDTH = 340;

export const SearchResultDetails = React.memo(() => {
    const dispatch = useDispatch();
    const selectedDoc = useSelectedDocContext();
    const searchConfig = useSelector((state: DefaultRootState) => state.config.uiConfig.search);
    const currentResultTab = useStateResultDetailsTab();
    const tabToRender: TabType = searchConfig.enabledDetailsTabs.includes(currentResultTab)
        ? currentResultTab
        : searchConfig.enabledDetailsTabs[0];
    const showDetails = useStateDocDetailsExpanded();
    const size: DynamicSize = useStateResultDetailsTabSize();


    const onExpand = () => {
        const newSate = !showDetails;
        dispatch(expandDocumentDetailsAction(newSate));
    };

    const renderSectionsTab = (): React.JSX.Element => {
        return <ContentSection/>;
    };

    const renderSpotlightTab = (): React.JSX.Element => {
        return <SpotlightSection/>;
    };

    const renderDetailsTab = (): React.JSX.Element => {
        return <DetailsSection/>;
    };

    const renderHibuAssistantTab = (): React.JSX.Element => {
        return <DetailsAssistant/>;
    };

    const renderContent = (): React.JSX.Element => {
        if (!selectedDoc) {
            return <></>;
        }

        switch (tabToRender) {
            case TabType.CONTENT: {
                return renderSectionsTab();
            }
            case TabType.SPOTLIGHT: {
                return renderSpotlightTab();
            }
            case TabType.DETAILS: {
                return renderDetailsTab();
            }
            case TabType.HIBU_ASSISTANT: {
                return renderHibuAssistantTab();
            }
        }
    };


    const getContainerSize = (minSize: number, maxSize: number, collapsedSize: number): number => {
        if (size.x) {
            if (!showDetails) {
                return collapsedSize;
            }
            if (size.x < minSize) {
                return minSize;
            }
            if (size.x > maxSize) {
                return maxSize;
            } else {
                return size.x;
            }
        }
        return minSize;
    };

    return (
        <>
            {searchConfig.enabledDetailsTabs && (
                <div id={'search-result-details'}
                     className={classNames(showDetails ? '' : 'hideContent')}
                     style={{
                         width: getContainerSize(
                             MIN_WIDTH,
                             window.innerWidth - (HIBU_FACET_COLUMN_MIN_WIDTH + HIBU_RESULT_LIST_COLUMN_MIN_WIDTH + SEARCH_PREVIEW_MIN_WIDTH),
                             COLLAPSED_WIDTH
                         )
                     }}
                >
                    <div
                        id={'details-collapsed'}
                        onClick={onExpand}
                        className={classNames(showDetails ? '' : 'hideContent')}
                    >
                        <SvgIcon icon={Icons.INFO} className={'details-collapsed-info'}/>
                        <SvgIcon icon={Icons.ARROWS} className={'details-collapsed-arrows'}/>
                    </div>
                    <div id={'details'} className={classNames(showDetails ? '' : 'hideContent')}>
                        <div id={'details_header'} className={classNames(selectedDoc ? '' : 'no-doc')}>
                            <DetailsTitle selectedDoc={selectedDoc} onExpand={onExpand}
                                          resultListFields={searchConfig.resultListFields}/>
                            <DetailsTabs selectedDoc={selectedDoc} enabledDetailsTabs={searchConfig.enabledDetailsTabs}
                                         tabToRender={tabToRender}/>
                        </div>
                        <div id={'details_content'}>
                            {renderContent()}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
});


type DetailsTitleProps = {
    selectedDoc: DeepReadonly<ProductDocument> | undefined;
    onExpand: () => void;
    resultListFields: ResultItemViewConfig;
};

const DetailsTitle = ({selectedDoc, onExpand, resultListFields}: DetailsTitleProps) => {
    const {t} = useTranslation();

    return (
        <div id={'details_title_container'}>
            <div id={'details_title'}>
                <FileTypeColumnControl
                    key={selectedDoc?.id || 'no-doc'}
                    doc={selectedDoc}
                    resultItemViewConfig={resultListFields}
                />
                <div>{selectedDoc?.title || t('search.details.title')}</div>
            </div>
            <HibuButton
                size={'sm'}
                onClick={onExpand}
                icon={Icons.X}
                tooltipKey={'search.details.hideDetails'}
                visible
                className={'details-close'}
                placement={'left'}
            />
        </div>
    );
};

type DetailsTabsProps = {
    selectedDoc: DeepReadonly<ProductDocument> | undefined;
    enabledDetailsTabs: TabType[];
    tabToRender: TabType;
};

const DetailsTabs = ({selectedDoc, enabledDetailsTabs, tabToRender}: DetailsTabsProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const navigate: NavigateFunction = useNavigate();

    const onClick = (tab: TabType) => {
        return (dispatch: (arg0: SearchPageAction) => void, getState: GetState) => {
            dispatch(switchResultDetailsTabAction(tab));
            changeUrl(getState, SearchPageType.BASIC, navigate);
        };
    };

    return (
        selectedDoc ?
            <div id={'details_tabs'}>
                {enabledDetailsTabs.includes(TabType.CONTENT) && (
                    <Button
                        onClick={() => dispatch(onClick(TabType.CONTENT))}
                        name={TabType.CONTENT}
                        className={'details-tab'}
                        active={tabToRender === TabType.CONTENT}
                    >
                        {t('search.details.tabs.sections')}
                    </Button>
                )}
                {enabledDetailsTabs.includes(TabType.SPOTLIGHT) && (
                    <Button
                        onClick={() => dispatch(onClick(TabType.SPOTLIGHT))}
                        name={TabType.SPOTLIGHT}
                        className={'details-tab'}
                        active={tabToRender === TabType.SPOTLIGHT}
                    >
                        {t('search.details.tabs.terms')}
                    </Button>
                )}
                {enabledDetailsTabs.includes(TabType.DETAILS) && (
                    <Button
                        onClick={() => dispatch(onClick(TabType.DETAILS))}
                        name={TabType.DETAILS}
                        className={'details-tab'}
                        active={tabToRender === TabType.DETAILS}
                    >
                        {t('search.details.tabs.details')}
                    </Button>
                )}
                {enabledDetailsTabs.includes(TabType.HIBU_ASSISTANT) && (
                    <Button
                        onClick={() => dispatch(onClick(TabType.HIBU_ASSISTANT))}
                        className={classNames('details-tab details-tab-assistant', 'hibu-button', 'md')}
                        active={tabToRender === TabType.HIBU_ASSISTANT}
                    >
                        <SvgIcon icon={Icons.AI_ASSISTANT}/>
                        {t('search.details.tabs.hibu-assistant')}
                    </Button>
                )}
            </div>
            :
            <p>{t('search.details.placeholder')}</p>
    );
};
